import React, { useState, useEffect } from 'react';
import { useMobileAuth } from '../MobileAuthProvider';
import { getOfflineActions, saveOfflineAction } from '../utils/offlineStorage';
import { 
  Calendar, Clock, MapPin, Users, CheckSquare, 
  ChevronRight, Filter, Search, Phone, Mail, WifiOff
} from 'lucide-react';

const MobileEvents = () => {
  const { user, isOnline } = useMobileAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming'); // upcoming, pending, history
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [offlineActions, setOfflineActions] = useState([]);

  useEffect(() => {
    fetchEvents();
    loadOfflineActions();
  }, [activeTab]);

  const loadOfflineActions = async () => {
    try {
      const actions = await getOfflineActions();
      setOfflineActions(actions.filter(action => action.type === 'events'));
    } catch (error) {
      console.error('Error loading offline actions:', error);
    }
  };

  const fetchEvents = async () => {
    try {
      const token = await user.getIdToken();
      const response = await fetch(`/api/mobile/events?status=${activeTab}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setEvents(data);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateTaskStatus = async (eventId, taskIndex) => {
    // Optimistic update
    setEvents(prev => prev.map(evt => {
      if (evt.id === eventId) {
        const newTimeline = [...evt.timeline];
        newTimeline[taskIndex] = { ...newTimeline[taskIndex], status: 'completed' };
        return { ...evt, timeline: newTimeline };
      }
      return evt;
    }));
    
    // Also update selectedEvent if it matches
    if (selectedEvent && selectedEvent.id === eventId) {
        const newTimeline = [...selectedEvent.timeline];
        newTimeline[taskIndex] = { ...newTimeline[taskIndex], status: 'completed' };
        setSelectedEvent({ ...selectedEvent, timeline: newTimeline });
    }

    const action = {
        id: Date.now().toString(),
        type: 'events',
        subType: 'task_update',
        eventId,
        taskIndex,
        status: 'completed',
        timestamp: new Date().toISOString(),
        synced: false
    };

    if (isOnline) {
        try {
            const token = await user.getIdToken();
            await fetch(`/api/mobile/events/${eventId}/tasks/${taskIndex}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ status: 'completed' })
            });
        } catch (error) {
            console.error('Error syncing event task:', error);
            saveOfflineAction(action);
            setOfflineActions([...offlineActions, action]);
        }
    } else {
        saveOfflineAction(action);
        setOfflineActions([...offlineActions, action]);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Detail View
  if (selectedEvent) {
    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        <div className="bg-white shadow-sm sticky top-0 z-10">
          <div className="px-4 py-3 flex items-center">
            <button 
              onClick={() => setSelectedEvent(null)}
              className="mr-3 text-gray-600"
            >
              ← Back
            </button>
            <h1 className="text-lg font-bold text-gray-900 truncate">{selectedEvent.name}</h1>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {/* Key Info Card */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex justify-between items-start mb-4">
              <div>
                <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mb-2 ${
                  selectedEvent.status === 'confirmed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {selectedEvent.status.toUpperCase()}
                </span>
                <div className="flex items-center text-gray-600 mb-1">
                  <Calendar className="w-4 h-4 mr-2" />
                  <span>{new Date(selectedEvent.date).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center text-gray-600 mb-1">
                  <Clock className="w-4 h-4 mr-2" />
                  <span>{selectedEvent.time}</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <MapPin className="w-4 h-4 mr-2" />
                  <span>{selectedEvent.location}</span>
                </div>
              </div>
              <div className="text-right">
                <div className="flex flex-col items-end">
                  <Users className="w-5 h-5 text-gray-400 mb-1" />
                  <span className="font-bold text-gray-900">{selectedEvent.guests}</span>
                  <span className="text-xs text-gray-500">Guests</span>
                </div>
              </div>
            </div>

            {/* Client Contact Actions */}
            <div className="flex space-x-3 pt-3 border-t border-gray-100">
              <a href={`tel:${selectedEvent.client.phone}`} className="flex-1 flex items-center justify-center py-2 bg-gray-50 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100">
                <Phone className="w-4 h-4 mr-2" />
                Call Client
              </a>
              <a href={`mailto:${selectedEvent.client.email}`} className="flex-1 flex items-center justify-center py-2 bg-gray-50 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100">
                <Mail className="w-4 h-4 mr-2" />
                Email
              </a>
            </div>
          </div>

          {/* Timeline / Run of Show */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <h2 className="font-semibold text-gray-900 mb-4">Timeline</h2>
            <div className="space-y-4 relative">
              <div className="absolute left-2.5 top-2 bottom-2 w-0.5 bg-gray-100" />
              {selectedEvent.timeline.length > 0 ? selectedEvent.timeline.map((item, idx) => (
                <div key={idx} className="relative pl-8">
                  <div className={`absolute left-0 top-1 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    item.status === 'completed' ? 'bg-green-100 border-green-500' : 'bg-white border-gray-300'
                  }`}>
                    {item.status === 'completed' && <div className="w-2 h-2 bg-green-500 rounded-full" />}
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{item.task}</p>
                      <p className="text-xs text-gray-500">{item.time}</p>
                    </div>
                    {item.status !== 'completed' && (
                      <button 
                        onClick={() => updateTaskStatus(selectedEvent.id, idx)}
                        className="px-3 py-1 bg-blue-50 text-blue-600 rounded text-xs font-medium"
                      >
                        Mark Done
                      </button>
                    )}
                  </div>
                </div>
              )) : (
                <p className="text-sm text-gray-500 text-center py-4">No timeline items yet.</p>
              )}
            </div>
          </div>

          {/* Staff & Notes (Placeholder) */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <h2 className="font-semibold text-gray-900 mb-2">Staffing</h2>
            <p className="text-sm text-gray-500">3 Servers, 1 Bartender assigned.</p>
            <button className="mt-3 w-full py-2 border border-gray-200 rounded-lg text-sm text-gray-600">
              View Staff Roster
            </button>
          </div>
        </div>
      </div>
    );
  }

  // List View
  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="px-4 py-3">
          <h1 className="text-xl font-bold text-gray-900">Events</h1>
        </div>
        
        {/* Tabs */}
        <div className="flex px-4 border-b border-gray-200 overflow-x-auto">
          {['upcoming', 'pending', 'history'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-3 text-sm font-medium border-b-2 whitespace-nowrap px-4 ${
                activeTab === tab 
                  ? 'border-blue-600 text-blue-600' 
                  : 'border-transparent text-gray-500'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="p-4 bg-gray-50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input 
              type="text" 
              placeholder="Search events..." 
              className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Offline Sync Status */}
      {!isOnline && offlineActions.length > 0 && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2">
          <div className="flex items-center justify-between">
            <p className="text-sm text-yellow-800">
              {offlineActions.length} changes will sync when online
            </p>
            <button
              onClick={() => {/* sync logic */}}
              className="text-sm text-yellow-600 underline"
            >
              Sync Now
            </button>
          </div>
        </div>
      )}

      {/* Events List */}
      <div className="px-4 space-y-3">
        {events.map(event => (
          <div 
            key={event.id}
            onClick={() => setSelectedEvent(event)}
            className="bg-white rounded-xl shadow-sm p-4 active:scale-[0.99] transition-transform"
          >
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-semibold text-gray-900">{event.name}</h3>
                <div className="flex items-center text-sm text-gray-500 mt-1">
                  <Calendar className="w-3 h-3 mr-1" />
                  <span>{new Date(event.date).toLocaleDateString()}</span>
                  <span className="mx-2">•</span>
                  <Clock className="w-3 h-3 mr-1" />
                  <span>{event.time}</span>
                </div>
                <div className="flex items-center text-sm text-gray-500 mt-1">
                  <MapPin className="w-3 h-3 mr-1" />
                  <span>{event.location}</span>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-300" />
            </div>
            <div className="mt-3 flex items-center justify-between">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                event.status === 'confirmed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
              }`}>
                {event.status}
              </span>
              <div className="flex items-center text-xs text-gray-500">
                <Users className="w-3 h-3 mr-1" />
                {event.guests}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MobileEvents;
